/**
 * Re-exports of generated DTOs and helper types for the notifications feature.
 *
 * Centralises all type imports so feature code imports from this module
 * instead of reaching directly into the generated `src/api/` folder.
 */

export type {
    ConversationDTO,
    ChatDTO,
    ChatMessageDTO,
    MessageReadByUserDTO,
    StatusPillDTO,
    ChatTimelineEntryDTO,
    CreateChatMessageDTO,
} from "@/api/data-contracts";

export {ChatType, MessageKind} from "@/api/data-contracts";

import type {ChatMessageDTO, ChatTimelineEntryDTO, MessageReadByUserDTO, StatusPillDTO,} from "@/api/data-contracts";

/**
 * Discriminator constants for timeline entries.
 * These values must match the backend `@JsonTypeInfo` discriminator values exactly.
 */
export const TIMELINE_ENTRY_TYPE = {
    MESSAGE: "MESSAGE",
    STATUS_PILL: "STATUS_PILL",
} as const;

/**
 * A timeline entry that carries a user or system message.
 * Discriminated on `type === "MESSAGE"`.
 */
export interface MessageTimelineEntry extends ChatTimelineEntryDTO {
    type: typeof TIMELINE_ENTRY_TYPE.MESSAGE;
    message: ChatMessageDTO;
}

/**
 * A timeline entry that carries a projected status pill (read-only, not persisted).
 * Discriminated on `type === "STATUS_PILL"`.
 */
export interface StatusPillTimelineEntry extends ChatTimelineEntryDTO {
    type: typeof TIMELINE_ENTRY_TYPE.STATUS_PILL;
    pill: StatusPillDTO;
}

/**
 * Union type for all possible timeline entries shown in a chat window.
 */
export type TimelineEntry = MessageTimelineEntry | StatusPillTimelineEntry;

/**
 * Returns true when the given timeline entry is a message entry.
 */
export function isMessageEntry(entry: ChatTimelineEntryDTO): entry is MessageTimelineEntry {
    return entry.type === TIMELINE_ENTRY_TYPE.MESSAGE;
}

/**
 * Returns true when the given timeline entry is a status pill entry.
 */
export function isStatusPillEntry(entry: ChatTimelineEntryDTO): entry is StatusPillTimelineEntry {
    return entry.type === TIMELINE_ENTRY_TYPE.STATUS_PILL;
}

/**
 * Attachment metadata embedded in a chat message.
 * Matches de.jhholding.boxhub.dtos.notifications.ChatMessageAttachmentMetadataDTO.
 */
export interface NotificationAttachmentMeta {
    id: number;
    filename: string;
    contentType: string;
    sizeBytes: number;
}

/**
 * Extended message shape returned by the notifications API.
 * The backend notifications.ChatMessageDTO uses `body`/`authorCompany`/`createdAt`
 * instead of the legacy `message`/`timestamp` fields.
 */
export interface NotificationsChatMessage {
    id?: number;
    /** USER_MESSAGE or SYSTEM_NOTE */
    kind?: string;
    /** Message text content (notifications API uses `body`) */
    body?: string;
    author?: { id?: number; firstName?: string; lastName?: string; email?: string };
    authorCompany?: { id?: number; name?: string };
    /** ISO datetime string (notifications API uses `createdAt`) */
    createdAt?: string;
    /** List of attachments on this message (new multi-attachment API). */
    attachments?: NotificationAttachmentMeta[];
    /** Legacy field — present in old chat messages */
    message?: string;
    /** Legacy field — present in old chat messages */
    timestamp?: string;
    /** Read state for all active users of the recipient's company — empty for own messages and SYSTEM_NOTEs. */
    readBy?: MessageReadByUserDTO[];
}
